import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { find } from 'lodash';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import ActionStatusModal from '../../ActionStatusModal';
import H3 from '../../H3';
import {
  MISSING_PRODUCTION_REQUIRED_ITEMS_IDS,
  missingItemsLabels,
  TP,
  PERFORMANCE_TYPES_ID,
  MISSING_PRODUCTION_ITEMS_IDS,
} from '../../../constants/consts';
import TextButton from '../../TextButton';
import Button from '../../Button';
import TitleErrorMessage from '../../TitleErrorMessage';
import usePermissions from '../../../utils/permissions';

const PublishProductionModal = ({ closeModal, production, publish, isPublishing, withCheckmark }) => {
  const { t } = useTranslation();
  const { isAdmin } = usePermissions();
  const [isPublishingConfirmed, setPublishingConfirmed] = React.useState(isAdmin || !withCheckmark);

  const isAllowedToPublish = React.useCallback(() => {
    const isRequeredValid = !production?.missing?.some(el =>
      Object.values(MISSING_PRODUCTION_REQUIRED_ITEMS_IDS).includes(el.id),
    );
    if (find(production?.performanceTypes, { id: PERFORMANCE_TYPES_ID.LIVESTREAM })) {
      return isRequeredValid;
    }
    return isRequeredValid && !find(production?.missing, { id: MISSING_PRODUCTION_ITEMS_IDS.DATES });
  }, [production]);

  const getMissingItems = React.useCallback(
    () => production?.missing.map(item => t(`${TP}.${missingItemsLabels[item?.id]}`)).join(', '),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [production],
  );

  const setConfirmation = e => {
    setPublishingConfirmed(e?.target?.checked);
  };

  return (
    <ActionStatusModal
      isOpened={Boolean(production)}
      onClose={closeModal}
      type={isAllowedToPublish() ? 'info' : 'warning'}
      title={
        <>
          {isAllowedToPublish() && production?.missing?.length > 0 && (
            <TitleErrorMessage>{`${t(`${TP}.m_MISSING`)} ${getMissingItems()}`}</TitleErrorMessage>
          )}
          <H3>{t(isAllowedToPublish() ? `${TP}.FN_EDITSEASON_PUBLISH_APPROVE_TITLE` : `${TP}.m_MISSING`)}</H3>
        </>
      }
      buttons={
        isAllowedToPublish()
          ? [
              <TextButton onClick={closeModal} key="publish-button-cancel">
                {t(`${TP}.m_CANCEL`)}
              </TextButton>,
              <Button onClick={publish} key="publish-button" isLoading={isPublishing} disabled={!isPublishingConfirmed}>
                {t(`${TP}.FN_PUBLISH`)}
              </Button>,
            ]
          : [
              <Button onClick={closeModal} key="publish-button-cancel">
                {t(`${TP}.m_CANCEL`)}
              </Button>,
            ]
      }
    >
      {isAllowedToPublish() ? <>{t(`${TP}.FN_EDITSEASON_PUBLISH_APPROVE_INFO`)}</> : getMissingItems()}
      {withCheckmark && isAllowedToPublish() && !isAdmin && (
        <>
          <div style={{ marginTop: 20 }} />
          <FormControlLabel
            id="publishingConfirmation"
            control={<Checkbox checked={isPublishingConfirmed} onChange={setConfirmation} color="secondary" />}
            label={t(`${TP}.FN_ARTIST_PUBLISHING_CONFIRMATION`)}
          />
        </>
      )}
    </ActionStatusModal>
  );
};

PublishProductionModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  production: PropTypes.object,
  publish: PropTypes.func.isRequired,
  isPublishing: PropTypes.bool,
  withCheckmark: PropTypes.bool,
};

PublishProductionModal.defaultProps = {
  production: null,
  isPublishing: false,
  withCheckmark: false,
};

export default PublishProductionModal;
